import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  ConfigProvider,
  Divider,
  Flex,
  Form,
  Input,
  InputNumber,
  Typography,
  Tooltip,
} from 'antd';
import TaggedInput from './TaggedInput';
import { API } from '../../../services';
import { ErrorMessageContext } from '../../../context/ErrorMessageContext';
import { BROADCAST_STATUS_KEYS, PAGE_KEYS } from '../../../utilities/constants';
import {
  sanitizeInput,
  getErrorMessage,
} from '../../../utilities/commonFunctions';
import { UserRolesAndPermissionsContext } from '../../../context/UserRolesAndPermissionsContext';

const { TextArea } = Input;
const { Title, Text } = Typography;

const showTextLength = ({ count, maxLength }) => (
  <Text>
    <small>
      {count}/{maxLength} characters
    </small>
  </Text>
);

const BroadcastForm = ({
  selectedRecipients,
  tripData,
  setTripData,
  selectedTripsCount,
  setBroadcastData,
  setInitailvehicleCount,
}) => {
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [form] = Form.useForm();
  const [tags, setTags] = useState(selectedRecipients);
  const [loading, setLoading] = useState(false);
  const { isViewOnly } = useContext(UserRolesAndPermissionsContext);
  const onFinish = async values => {
    const { message, repeatCount, recipients } = values;
    try {
      setLoading(true);
      const payload = {
        targets: recipients?.map(recipient => ({
          device_id: recipient?.device_id,
          trip_id: recipient?.trip_id,
        })),
        triggerfrequency: repeatCount,
        text_message: sanitizeInput(message),
        sent_by: sessionStorage.getItem('email'),
      };
      const { data } = await API.postBroadcastMessage(payload);
      addErrorMessage(data?.message, 'success');
      setInitailvehicleCount(recipients?.length);
      setBroadcastData(
        recipients?.map(recipient => {
          return {
            device_id: recipient?.device_id,
            vehicle_number: recipient?.vehicle_number,
            status: BROADCAST_STATUS_KEYS.INPROGRESS,
            text_message: message,
            trigger_frequency: repeatCount,
            key: recipient?.key,
          };
        })
      );
      clearAll();
      form.resetFields();
    } catch (error) {
      addErrorMessage(
        getErrorMessage(
          error,
          'Unable to broadcast the message! Please try again after sometime. '
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const clearAll = () => {
    setTripData(prev => prev?.map(value => ({ ...value, checked: false })));
    setTags([]);
  };

  const cancelForm = () => {
    form.resetFields();
    clearAll();
  };

  useEffect(() => {
    setTags(selectedRecipients);
    form.setFieldsValue({ recipients: selectedRecipients });
  }, [selectedRecipients]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Typography: {
            titleMarginTop: 0,
            fontSize: '1rem',
          },
          Form: {
            itemMarginBottom: 0,
          },
          Input: {
            colorBgContainer: 'var(--broadcast_form-input-bg-color, #2E3033)',
            paddingBlock: '0.75rem',
          },
          InputNumber: {
            colorBgContainer: 'var(--broadcast_form-input-bg-color, #2E3033)',
            fontSize: '1rem',
            colorBorder: 'transparent',
            colorTextPlaceholder: 'var(--placeholder-text-color, #ffffff80)',
            paddingBlock: '0.75rem',
          },
          TextArea: {
            colorTextPlaceholder: 'var(--placeholder-text-color, #ffffff80)',
          },
        },
      }}
    >
      <Card variant="borderless" className="broadcast-form-card full-height">
        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          className="full-height broadcast-form"
          scrollToFirstError
          requiredMark={false}
        >
          <Form.Item
            className="recipient-form-item"
            name="recipients"
            rules={[
              {
                required: true,
                message: 'Please enter one or more recipients',
              },
            ]}
            label={
              <Flex className="full-width" vertical gap={10}>
                <Flex
                  justify="space-between"
                  wrap="wrap"
                  align="center"
                  className="full-width"
                >
                  <Title level={4} data-cy="title-recipient-count">
                    {selectedTripsCount || 'No'} Recipients{' '}
                    {!selectedTripsCount && 'Selected'}
                  </Title>
                  <Button
                    onClick={clearAll}
                    disabled={!selectedTripsCount}
                    data-cy="btn-clear-selection"
                  >
                    Clear selection
                  </Button>
                </Flex>
                <Divider />
              </Flex>
            }
          >
            <TaggedInput
              dataSource={tripData}
              placeholder="Enter vehicle number"
              onChange={recipients => form.setFieldsValue({ recipients })}
              tags={tags}
              setTags={setTags}
              setTripData={setTripData}
            />
          </Form.Item>
          <Form.Item
            name="message"
            className="message-form-item"
            rules={[
              {
                required: true,
                message: 'Please enter message',
              },
            ]}
            label={
              <Flex className="full-width" vertical gap={10}>
                <Title level={4}>Message</Title>
                <Divider />
              </Flex>
            }
          >
            <TextArea
              autoSize
              placeholder="Type your message here&#10;e.g: Hello from ridebuddy"
              rows={4}
              maxLength={500}
              count={{
                max: 500,
                show: showTextLength,
              }}
            />
          </Form.Item>
          <Form.Item
            name="repeatCount"
            className="frequency-form-item"
            rules={[
              {
                required: true,
                message: 'Please enter frequency',
              },
            ]}
            label={
              <Flex className="full-width" vertical gap={10}>
                <Title level={4}>Define Frequency</Title>
                <Divider />
              </Flex>
            }
          >
            <InputNumber
              min={1}
              controls={false}
              placeholder="number of times e.g.: 3"
              addonBefore={
                <Text>
                  <b>Repeat message</b>
                </Text>
              }
              addonAfter={
                <Text>
                  <b>time(s)</b>
                </Text>
              }
              type="number"
            />
          </Form.Item>
          <Form.Item>
            <Flex gap={10} justify="end" wrap="wrap">
              <Tooltip
                title={
                  isViewOnly(PAGE_KEYS.BROADCAST) &&
                  'You do not have permission'
                }
                placement="top"
              >
                <Button
                  type="primary"
                  htmlType="submit"
                  data-cy="btn-send-message"
                  loading={loading}
                  disabled={loading || isViewOnly(PAGE_KEYS.BROADCAST)}
                >
                  Send Message
                </Button>
              </Tooltip>
              <Button
                htmlType="button"
                onClick={cancelForm}
                disabled={loading}
                data-cy="btn-cancel"
              >
                Cancel
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Card>
    </ConfigProvider>
  );
};

BroadcastForm.propTypes = {
  selectedRecipients: PropTypes.array,
  tripData: PropTypes.array,
  setTripData: PropTypes.func.isRequired,
  selectedTripsCount: PropTypes.number,
  setBroadcastData: PropTypes.func.isRequired,
  setInitailvehicleCount: PropTypes.func.isRequired,
};

export default BroadcastForm;
